import { c as _c } from "react/compiler-runtime";
import { usePathname, useSearchParams } from "next/navigation";
import { useCallback, useEffect, useRef } from "react";
import { z } from "zod";
import { QUERY_CACHE_TIME } from "@/components/query-provider";
import { readonlyURLSearchParamsToStr } from "@/lib/type-safe-stringify";

// Flag to track if navigation has occurred (cleared on page reload)
let isFirstPageLoad = true;
const scrollDataSchema = z.object({
  scrollY: z.number(),
  timestamp: z.number()
});
type ScrollData = z.infer<typeof scrollDataSchema>;

/**
 * Hook to save and restore scroll position when navigating between pages.
 * Uses sessionStorage to persist scroll position with route-specific keys.
 * Does NOT restore scroll position if:
 * - The page was reloaded
 * - Too much time has passed (data likely garbage collected)
 */
export function useScrollRestoration() {
  const $ = _c(9);
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const hasRestoredRef = useRef(false);
  let t0;
  if ($[0] !== searchParams) {
    t0 = readonlyURLSearchParamsToStr(searchParams);
    $[0] = searchParams;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const scrollKey = `scroll-${pathname}-${t0}`;
  const isFirstPageLoadRef = useRef(isFirstPageLoad);
  let t1;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = [];
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  useEffect(_temp, t1);
  let t2;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = [];
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  useEffect(_temp2, t2);
  let t3;
  let t4;
  if ($[4] !== scrollKey) {
    t3 = () => {
      const handleScroll = () => {
        const scrollData = {
          scrollY: window.scrollY,
          timestamp: Date.now()
        };
        sessionStorage.setItem(scrollKey, JSON.stringify(scrollData));
      };
      window.addEventListener("scroll", handleScroll, {
        passive: true
      });
      return () => {
        window.removeEventListener("scroll", handleScroll);
      };
    };
    t4 = [scrollKey];
    $[4] = scrollKey;
    $[5] = t3;
    $[6] = t4;
  } else {
    t3 = $[5];
    t4 = $[6];
  }
  useEffect(t3, t4);
  let t5;
  if ($[7] !== scrollKey) {
    t5 = () => {
      if (hasRestoredRef.current) {
        return;
      }
      if (isFirstPageLoadRef.current) {
        hasRestoredRef.current = true;
        return;
      }
      const saved = sessionStorage.getItem(scrollKey);
      if (saved === null) {
        hasRestoredRef.current = true;
        return;
      }
      try {
        const parsed = JSON.parse(saved);
        const result = scrollDataSchema.safeParse(parsed);
        if (!result.success) {
          hasRestoredRef.current = true;
          return;
        }
        const scrollData_0 = result.data;
        const timeSinceSaved = Date.now() - scrollData_0.timestamp;
        if (timeSinceSaved > QUERY_CACHE_TIME) {
          hasRestoredRef.current = true;
          return;
        }
        requestAnimationFrame(() => {
          window.scrollTo(0, scrollData_0.scrollY);
          hasRestoredRef.current = true;
        });
      } catch {
        hasRestoredRef.current = true;
      }
    };
    $[7] = scrollKey;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  return t5;
}
function _temp2() {
  if ("scrollRestoration" in window.history) {
    const originalScrollRestoration = window.history.scrollRestoration;
    window.history.scrollRestoration = "manual";
    return () => {
      window.history.scrollRestoration = originalScrollRestoration;
    };
  }
}
function _temp() {
  isFirstPageLoad = false;
}